import {
  addMonths,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ScheduleTypeEnum } from '../../../../../../js/api';
import { Drawer, DrawerBody } from '../../../../components/drawer';
import type { StatusCodes } from '../../../../constants/workflows';
import { useIsMdUp } from '../../../../hooks/useIsMdUp';
import { parseApiLocalDate } from '../../../../utils/date';
import { getShiftStatusBucket } from '../../../../utils/globalStyles';
import { AssignmentDrawer } from '../../Assignments/AssignmentDrawer';
import { CalendarHeader } from './CalendarHeader';
import { ScheduleCalendarGrid } from './ScheduleCalendarGrid';
import { useDebouncedScheduleSave } from './useDebouncedScheduleSave';
import { useProviderAssignmentShifts } from './useProviderAssignmentShifts';
import { useProviderSchedule } from './useProviderSchedule';
import { useScheduleTypeMutation } from './useScheduleType';

interface Props {
  availabilityUrl: string;
}

// Data window grows in chunks as the user navigates toward an edge. Initial
// window matches the design (-2mo / +6mo). When currentMonth is within
// EDGE_THRESHOLD_MONTHS of either bound, the window expands by
// EXPANSION_MONTHS in that direction. Each expansion changes the query key,
// which triggers a fresh react-query fetch for the wider window; previous
// results stay cached so prior data stays available without re-rendering.
const INITIAL_PAST_MONTHS = 2;
const INITIAL_FUTURE_MONTHS = 6;
const EXPANSION_MONTHS = 2;
const EDGE_THRESHOLD_MONTHS = 1;
const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Top-level composition for the Provider Home schedule calendar.
 *
 * Owns:
 *  - Controlled currentMonth state.
 *  - The dynamic data window (-2mo / +6mo to start; expands in 2-month
 *    increments when the user navigates toward an edge).
 *  - The optimistic save state via useDebouncedScheduleSave.
 *  - Which assignment drawer (if any) is open.
 *  - Surfacing save errors as toasts using the project's react-toastify pattern.
 *
 * Mode (schedule_type) is read from the schedule query response and drives
 * which row kind the user is editing:
 *   - OPEN  → block rows (is_blocked=True); blocks render red, everything
 *             else in the window paints green-available.
 *   - CLOSED/STRICT → allow rows (is_blocked=False); current behavior.
 * Switching schedule_type force-flushes the pending buffer first so queued
 * edits land in the right bucket.
 *
 * Booked-day data is driven by individual AssignmentShift records (not
 * assignment date ranges) — see useProviderAssignmentShifts for why.
 */
export const ProviderScheduleCalendar = ({ availabilityUrl }: Props) => {
  const isMdUp = useIsMdUp();
  const visibleMonthsCount = isMdUp ? 2 : 1;

  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());
  const [openAssignmentId, setOpenAssignmentId] = useState<string | null>(null);

  const [windowStart, setWindowStart] = useState<Date>(() =>
    subMonths(new Date(), INITIAL_PAST_MONTHS)
  );
  const [windowEnd, setWindowEnd] = useState<Date>(() =>
    addMonths(new Date(), INITIAL_FUTURE_MONTHS)
  );

  // Expand the window when currentMonth approaches an edge. The functional
  // setState form keeps this safe even if two edges are crossed in rapid
  // succession.
  useEffect(() => {
    const monthsFromStart = differenceInCalendarMonths(currentMonth, windowStart);
    const monthsToEnd = differenceInCalendarMonths(windowEnd, currentMonth);
    if (monthsFromStart <= EDGE_THRESHOLD_MONTHS) {
      setWindowStart((prev) => subMonths(prev, EXPANSION_MONTHS));
    }
    if (monthsToEnd <= EDGE_THRESHOLD_MONTHS) {
      setWindowEnd((prev) => addMonths(prev, EXPANSION_MONTHS));
    }
  }, [currentMonth, windowStart, windowEnd]);

  const start = format(windowStart, ISO_DATE_FORMAT);
  const end = format(windowEnd, ISO_DATE_FORMAT);

  const { data: scheduleData, isLoading: isScheduleLoading } = useProviderSchedule({ start, end });
  const { data: shiftsData, isLoading: isShiftsLoading } = useProviderAssignmentShifts({
    start,
    end,
  });

  const scheduleType: ScheduleTypeEnum = scheduleData?.schedule_type ?? ScheduleTypeEnum.OPEN;
  const isOpenMode = scheduleType === ScheduleTypeEnum.OPEN;

  // Which array we are editing depends on schedule_type. Under OPEN the
  // provider is recording blocks; under CLOSED/STRICT they're recording
  // allows. The bulk mutation gets `is_blocked=isOpenMode` so writes land
  // in the matching bucket.
  const save = useDebouncedScheduleSave({ isBlocked: isOpenMode });
  const scheduleTypeMutation = useScheduleTypeMutation();

  const serverDates = useMemo<string[]>(
    () => (isOpenMode ? (scheduleData?.block_dates ?? []) : (scheduleData?.allow_dates ?? [])),
    [scheduleData, isOpenMode]
  );
  const shifts = useMemo(() => shiftsData ?? [], [shiftsData]);

  // Both badge counts cover the entire visible timeframe (1 month on
  // mobile, 2 months on md+), not just the anchor month. The data window
  // is wider than what's visible, so we filter to the visible range here.
  const visibleStart = useMemo(() => startOfMonth(currentMonth), [currentMonth]);
  const visibleEnd = useMemo(
    () => endOfMonth(addMonths(currentMonth, visibleMonthsCount - 1)),
    [currentMonth, visibleMonthsCount]
  );

  // Under OPEN, the badge reports "days blocked" (the edited set IS blocks).
  // Under CLOSED/STRICT it reports "days available" (the edited set IS allows).
  // The badge label flip lives in ScheduleCalendarGrid; the count is the same
  // shape either way.
  const editedDaysInView = useMemo(() => {
    return serverDates.reduce((count, iso) => {
      const parsed = parseISO(iso);
      return parsed >= visibleStart && parsed <= visibleEnd ? count + 1 : count;
    }, 0);
  }, [serverDates, visibleStart, visibleEnd]);

  // Distinct start_on dates across every shift whose status buckets to
  // 'confirmed'. Mirrors how the calendar grid paints green cells — one
  // count per visible confirmed day, deduped when two confirmed shifts
  // share a start_on (e.g., a split day with two short shifts).
  const confirmedDaysCount = useMemo(() => {
    const days = new Set<string>();
    shifts.forEach((shift) => {
      if (getShiftStatusBucket(shift.current_status.code as StatusCodes) !== 'confirmed') return;
      const startDate = parseApiLocalDate(shift.start_on);
      if (!startDate) return;
      if (startDate < visibleStart || startDate > visibleEnd) return;
      days.add(format(startDate, ISO_DATE_FORMAT));
    });
    return days.size;
  }, [shifts, visibleStart, visibleEnd]);

  // Toggle handler — decides add vs remove based on current displayed state.
  // Displayed = (serverDates ∪ pendingAdds) − pendingRemoves.
  const serverDateSet = useMemo(() => new Set(serverDates), [serverDates]);
  const handleToggleDate = (iso: string) => {
    const isDisplayed =
      (serverDateSet.has(iso) || save.pendingAdds.has(iso)) && !save.pendingRemoves.has(iso);
    if (isDisplayed) {
      save.enqueueRemove(iso);
    } else {
      save.enqueueAdd(iso);
    }
  };

  // Schedule_type flip. Flush any queued edits FIRST so they land in the
  // old mode's bucket; then PATCH the type. The mutation hook invalidates
  // the schedule query on success so the next render picks up the new
  // active set.
  const handleScheduleTypeChange = (next: ScheduleTypeEnum) => {
    if (next === scheduleType) return;
    scheduleTypeMutation.mutate({
      scheduleType: next,
      flushPending: save.flushNow,
    });
  };

  // Surface save errors via the project's existing toast utility.
  useEffect(() => {
    if (save.saveError) {
      toast.error('Could not save schedule changes. Please try again.');
    }
  }, [save.saveError]);

  useEffect(() => {
    if (scheduleTypeMutation.error) {
      toast.error('Could not update schedule type. Please try again.');
    }
  }, [scheduleTypeMutation.error]);

  const isInitialLoading =
    (isScheduleLoading && scheduleData === undefined) ||
    (isShiftsLoading && shiftsData === undefined);

  if (isInitialLoading) {
    return (
      <div className="tw:py-6" aria-busy="true" aria-live="polite">
        <div className="tw:h-6 tw:w-48 tw:bg-zinc-200 tw:animate-pulse tw:rounded tw:mb-4" />
        <div className="tw:h-64 tw:w-full tw:bg-zinc-100 tw:animate-pulse tw:rounded" />
      </div>
    );
  }

  return (
    <>
      <CalendarHeader
        availabilityUrl={availabilityUrl}
        isMdUp={isMdUp}
        scheduleType={scheduleType}
        onScheduleTypeChange={handleScheduleTypeChange}
        scheduleTypeChanging={scheduleTypeMutation.isLoading}
      />
      <ScheduleCalendarGrid
        currentMonth={currentMonth}
        onMonthChange={setCurrentMonth}
        windowStart={windowStart}
        windowEnd={windowEnd}
        scheduleType={scheduleType}
        serverDates={serverDates}
        pendingAdds={save.pendingAdds}
        pendingRemoves={save.pendingRemoves}
        shifts={shifts}
        onToggleDate={handleToggleDate}
        onAssignmentClick={setOpenAssignmentId}
        availableDaysInView={editedDaysInView}
        confirmedDaysCount={confirmedDaysCount}
      />
      <Drawer
        open={openAssignmentId !== null}
        onClose={() => setOpenAssignmentId(null)}
        width="3xl"
        mode="modal"
      >
        <DrawerBody className="tw:!text-sm/6">
          {openAssignmentId && (
            <AssignmentDrawer
              key={openAssignmentId}
              assignmentId={openAssignmentId}
              onClose={() => setOpenAssignmentId(null)}
            />
          )}
        </DrawerBody>
      </Drawer>
    </>
  );
};
